import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    CustomerPartCollectionModel,
    CustomerPartModel,
    GetCustomerPartsApiParameter,
} from "@midland-industries/Services/CustomerPartService";
import { CustomerPartsState } from "@midland-industries/Store/Pages/CustomerPartNumber/CustomerPartsState";
import { Draft } from "immer";

const initialCustomDataState: CustomerPartsState = {
    isLoading: {},
    byId: {},
    dataViews: {},
    errorStatusCodeById: {},
};

const customerParts = createTypedReducerWithImmer(initialCustomDataState, {
    "Xcentium/CustomerParts/BeginLoadCustomerParts": (
        draft: Draft<CustomerPartsState>,
        action: { parameter: GetCustomerPartsApiParameter },
    ) => {
        setDataViewLoading(draft, action.parameter);
    },

    "Xcentium/CustomerParts/CompleteLoadCustomerParts": (
        draft: Draft<CustomerPartsState>,
        action: { parameter: GetCustomerPartsApiParameter; collection: CustomerPartCollectionModel },
    ) => {
        setDataViewLoaded(draft, action.parameter, action.collection, collection => collection.customerParts!);

        if (draft.failedToLoadCustomerParts) {
            draft.failedToLoadCustomerParts = false;
        }
    },

    "Xcentium/CustomerParts/FailedToLoadCustomerParts": (draft: Draft<CustomerPartsState>) => {
        draft.failedToLoadCustomerParts = true;
    },
    "Xcentium/CustomerParts/BeginLoadCustomerPartByProductNumber": (
        draft: Draft<CustomerPartsState>,
        action: { id: string },
    ) => {
        draft.isLoading[action.id] = true;
    },

    "Xcentium/CustomerParts/CompleteLoadCustomerPartByProductNumber": (
        draft: Draft<CustomerPartsState>,
        action: { model: CustomerPartModel },
    ) => {
        delete draft.isLoading[action.model.id];
        draft.byId[action.model.id] = action.model;
        if (draft.errorStatusCodeById) {
            delete draft.errorStatusCodeById[action.model.id];
        }
    },

    "Xcentium/CustomerParts/FailedToLoadCustomerPartByProductNumber": (
        draft: Draft<CustomerPartsState>,
        action: { id: string; status: number },
    ) => {
        delete draft.isLoading[action.id];
        if (draft.errorStatusCodeById) {
            draft.errorStatusCodeById[action.id] = action.status;
        }
    },
    "Xcentium/CustomerParts/BeginUpdateCustomerPart": (draft: Draft<CustomerPartsState>, action: { id: string }) => {
        draft.isLoading[action.id] = true;
    },

    "Xcentium/CustomerParts/CompleteUpdateCustomerPart": (
        draft: Draft<CustomerPartsState>,
        action: { model: CustomerPartModel },
    ) => {
        delete draft.isLoading[action.model.id];
        draft.byId[action.model.id] = action.model;
        if (draft.errorStatusCodeById) {
            delete draft.errorStatusCodeById[action.model.id];
        }
    },

    "Xcentium/CustomerParts/FailedToUpdateCustomerPart": (
        draft: Draft<CustomerPartsState>,
        action: { id: string; status: number },
    ) => {
        delete draft.isLoading[action.id];
        if (draft.errorStatusCodeById) {
            draft.errorStatusCodeById[action.id] = action.status;
        }
    },
    "Xcentium/CustomerParts/BeginDeleteCustomerPart": (draft: Draft<CustomerPartsState>, action: { id: string }) => {
        draft.isLoading[action.id] = true;
    },
    "Xcentium/CustomerParts/CompleteDeleteCustomerPart": (draft: Draft<CustomerPartsState>, action: { id: string }) => {
        delete draft.isLoading[action.id];
    },
});

(reducers as any).customerParts = customerParts;

export type CustomState = {
    customerParts: CustomerPartsState;
} & ApplicationState;

type Reducers = {
    customerParts: typeof customerParts;
};

export type CustomActions = Parameters<Reducers[keyof Reducers]>[1] | AnyAction;
